import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
	getQuizzes,
	updateQuiz,
	addQuiz as dispatchAddQuiz,
} from '../../../../../store/actions/admin/quizzes';
import {
	CreateQuiz,
	PageModule,
	Quiz,
	QuizQuestion
} from '../../../../../types/working-model';
import Icon, { ICONS, COLORS } from '../../../../general-ui/icon';
import LargeButton from '../../../../general-ui/button/large-button';
import { List } from 'react-virtualized';
import ModalComponent from '../../../../general-ui/modal/modal';
import { useParams } from 'react-router';
import Checkbox from '../../../../general-ui/checkbox/checkbox';
import TextInput from '../../../../general-ui/text-input/text';
import { GetDefaultQuiz } from '../../../../../store/utils/create-event';
import { showAlert } from '../../../../general-ui/alert/alert-service';
import Switch from '../../../../general-ui/switch/switch';
import { CSS } from '@dnd-kit/utilities';
import './quizzes.scss';
import '../../../../live-event/session/session-modules/quizzes/quizzes.scss';

import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	rectSortingStrategy,
	useSortable,
} from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
	GetNewOption,
	GetNewQuestion,
	InsertQuiz,
	UpdateQuiz,
} from '../../../../../connection/quizzes';
import WaitingIndicator from '../../../../general-ui/waiting-indicator/waiting-indicator';
import { updatePageModule } from '../../../../../store/actions/admin/create-event/session';
import { updateTranslateKey } from '../../../../../utils/utils';
import EditableCustomTitle from '../../../../general-ui/editable-title/editable-custom-title';
import { useTranslation } from 'react-i18next';
// import RenderQuiz from './quiz-row';
import Controls from '../session-module-controls/controls';
import ReactSelect from 'react-select';
import { reactSelectStyles } from '../../../../general-ui/react-select/react-select-styles';
import QuizItem from './quiz';
import { TemplateClassNames } from '../../../../../types/template-layouts';
import { useTypedSelector } from '../../../../../store/reducers/use-typed-selector';
import { ChecklistTitles } from '../../../../../store/reducers/admin/assistant';
import { addChecklistItem } from '../../../../../store/actions/authentication';
import { getStylingOverrides } from '../../../../live-event/utils';
import classNames from 'classnames';
import { ParamsProps } from '../../../../live-event/live-event';

interface Props {
	page_module: PageModule;
	template: TemplateClassNames;
}

enum QuizSelectionSteps {
	select,
	create,
	edit,
	questionsList,
	question,
}

export default function Quizzes(props: Props): JSX.Element | null {
	const { page_module, template } = props;
	const {
		workingSession,
		user,
		token,
		quizzes
	} = useTypedSelector(state => ({
		workingSession: state.CreateSessionReducer.workingSession,
		user: state.AuthReducer.user,
		token: state.AuthReducer.token,
		quizzes: state.QuizzesReducer.quizzes,
	}));
	const { language } = useParams() as ParamsProps;

	const { t } = useTranslation(['session']);

	const { backgroundColor, backgroundImg } = getStylingOverrides(page_module.styling_overrides);

	const [modalOpen, setModalOpen] = useState(false);
	const [workingQuiz, setWorkingQuiz] = useState<Quiz | CreateQuiz | null>(
		null
	);
	const [workingQuestion, setWorkingQuestion] = useState<QuizQuestion | null>(
		null
	);
	const [selectedQuizzes, setSelectedQuizzes] = useState<number[]>([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeStep, setActiveStep] = useState<QuizSelectionSteps>(
		QuizSelectionSteps.select
	);
	const [saving, setSaving] = useState(false);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getQuizzes(token!));
	}, [dispatch, token]);

	function addQuiz() {
		setModalOpen(true);
	}

	function closeModal() {
		setModalOpen(false);
		setActiveStep(QuizSelectionSteps.select);
		setWorkingQuiz(null);
	}

	// on first render, set selected quizzes based on saved module content
	useEffect(() => {
		setSelectedQuizzes(page_module?.modules?.map((quiz: Quiz) => {
			return quiz.quiz;
		}) ?? []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const checkQuiz = useCallback(
		(quiz: number) => {
			return () => {
				if (selectedQuizzes.includes(quiz)) {
					setSelectedQuizzes((selectedQuizzes) =>
						selectedQuizzes.filter((id) => id !== quiz)
					);
				} else {
					const selQuiz = quizzes.find(_quiz => _quiz.quiz === quiz);

					UpdateQuiz(token!, selQuiz!, workingSession?.languages).then(result => {
						dispatch(updateQuiz(result));
					});

					setSelectedQuizzes((selectedQuizzes) => [
						...selectedQuizzes,
						quiz,
					]);
				}
			};
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[selectedQuizzes]
	);

	const visibleQuizzes = useMemo(() => {
		if (!searchTerm) { return quizzes; }

		return quizzes.filter((quiz: Quiz) =>
			quiz.title.base
				.toLowerCase()
				.trim()
				.replace(/ /g, '')
				.includes(searchTerm)
		);
	}, [searchTerm, quizzes]);

	function editQuiz(quiz: Quiz) {
		return () => {
			setWorkingQuiz(quiz);
			setActiveStep(QuizSelectionSteps.edit);
		};
	}

	const RenderQuiz = useCallback(
		(props: any) => {
			const {
				key, // Unique key within array of rows
				index, // Index of row within collection
				style,
			} = props;

			const quiz = visibleQuizzes[index];

			const selected = selectedQuizzes.includes(quiz.quiz);

			return (
				quiz && (
					<div className="quiz-row-container" key={key} style={style}>
						<div className={`quiz-row ${selected ? 'selected' : ''}`}>
							<div className="checkbox-container">
								<Checkbox
									onChange={checkQuiz(quiz.quiz)}
									checked={selected}
									value={quiz.quiz}
									color={'white'}
									activeColor={'cyan'}
								/>
							</div>
							<div className="quiz-data">
								<p>{quiz.title.base}</p>
								<span className="question-count">{`${quiz
									.questions?.length ?? '0'} question${quiz.questions?.length === 1 ? '' : 's'
									}`}</span>
							</div>
							<div className="quiz-actions">
								<button
									className="no-style"
									onClick={editQuiz(quiz)}
								>
									<Icon
										name={ICONS.EDIT}
										size={16}
										color={COLORS.WHITE}
									/>
								</button>
							</div>
						</div>
					</div>
				)
			);
		},
		[selectedQuizzes, visibleQuizzes, checkQuiz]
	);

	const doneSelectingQuizzes = useCallback(() => {
		dispatch(
			updatePageModule({
				...page_module,
				modules: selectedQuizzes.map((selectedQuiz) =>
					quizzes.find((quiz) => quiz.quiz === selectedQuiz)
				),
				content_modules: selectedQuizzes,
			})
		);

		// ASSISTANT CHECKLIST ITEM: complete users checklist item for editing a section
		if (
			user &&
			!user.onboarded.includes(ChecklistTitles.editSection)
		) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editSection));

		setWorkingQuestion(null);
		setWorkingQuiz(null);
		setModalOpen(false);
	}, [selectedQuizzes, quizzes, page_module, dispatch, token, user]);

	function handleSearch({
		target: { value },
	}: React.ChangeEvent<HTMLInputElement>) {
		setSearchTerm(
			value
				.toLowerCase()
				.trim()
				.replace(/ /g, '')
		);
	}

	const createNewQuiz = useCallback(() => {
		if (language === workingSession?.default_language) {
			setWorkingQuiz(GetDefaultQuiz());
			setActiveStep(QuizSelectionSteps.create);
		} else {
			showAlert({
				message: 'Unable to Create',
				description:
					'You are only able to create new content from the default selected language.',
				type: 'error',
				duration: 5000,
			});
		}
	}, [language, workingSession?.default_language]);

	const setWorkingQuizTransKey = useCallback(
		(
			key: 'title' | 'success_message' | 'failure_message',
			value: string
		) => {
			setWorkingQuiz((workingQuiz) => {
				if (!workingQuiz) { return null; }

				return {
					...workingQuiz,
					[key]: updateTranslateKey({
						translateString: workingQuiz[key],
						input: value,
						baseLanguage: workingSession!.default_language,
						language
					}),
				};
			});
		},
		[language, workingSession]
	);

	const setWorkingQuizTitle = useCallback(
		({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingQuizTransKey('title', value);
		},
		[setWorkingQuizTransKey]
	);

	const setWorkingQuizSuccess = useCallback(
		({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingQuizTransKey('success_message', value);
		},
		[setWorkingQuizTransKey]
	);

	const setWorkingQuizFailure = useCallback(
		({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingQuizTransKey('failure_message', value);
		},
		[setWorkingQuizTransKey]
	);

	function setSuccessThreshold({
		target: { valueAsNumber },
	}: React.ChangeEvent<HTMLInputElement>) {
		setWorkingQuiz((workingQuiz) => {
			if (!workingQuiz) { return null; }

			return {
				...workingQuiz,
				success_threshold: valueAsNumber,
			};
		});
	}

	function toggleAllowRetake(value: any, isOn: boolean) {
		setWorkingQuiz((workingQuiz) => {
			if (!workingQuiz) { return null; }

			return {
				...workingQuiz,
				allow_retake: isOn,
			};
		});
	}

	function goToQuestions() {
		setWorkingQuestion(null);
		setActiveStep(QuizSelectionSteps.questionsList);
	}

	const handleDragEnd = useCallback(
		(event: any) => {
			if (!workingQuiz) { return; }
			const questions = [...workingQuiz.questions];

			const startIndex = questions.findIndex(
				(question: QuizQuestion) =>
					Number(question.quiz_question) === Number(event.active.id)
			);
			const newIndex = questions.findIndex(
				(question: QuizQuestion) =>
					Number(question.quiz_question) === Number(event.over.id)
			);

			const [question] = questions.splice(startIndex, 1);
			questions.splice(newIndex, 0, question);

			setWorkingQuiz((quiz) => {
				if (!quiz) {
					return null;
				}

				return { ...quiz, questions };
			});
		},
		[workingQuiz]
	);

	const editQuestion = useCallback((question: QuizQuestion) => {
		return () => {
			setWorkingQuestion(question);
			setActiveStep(QuizSelectionSteps.question);
		};
	}, []);

	const removeQuestion = useCallback((questionToRemove: QuizQuestion) => {
		// remove question from current list
		const newQuestions = workingQuiz?.questions.filter((question: QuizQuestion) => question !== questionToRemove);
		setWorkingQuiz((workingQuiz) => {
			// return if no curent working list or if new list undefined
			if (!workingQuiz || !newQuestions) { return null; }
			// set new list of questions after removing passed in question
			return {
				...workingQuiz,
				questions: newQuestions
			};
		});
	}, [workingQuiz]);

	const addQuestion = useCallback(async () => {
		const newQuestion = await GetNewQuestion(token!);
		setWorkingQuiz((workingQuiz) => {
			if (!workingQuiz) { return null; }

			return {
				...workingQuiz,
				questions: [...workingQuiz.questions, newQuestion],
			};
		});
	}, [token]);

	const setQuestionType = useCallback((value: any) => {
		setWorkingQuestion((question) => {
			if (!question) { return null; }

			return {
				...question,
				multiple_choice: value === 'multiple-choice',
			};
		});
	}, []);

	const addOption = useCallback(async () => {
		const newOption = await GetNewOption(token!);

		setWorkingQuestion((question) => {
			if (!question) { return null; }

			return {
				...question,
				options: [...question.options, newOption],
			};
		});
	}, [token]);

	const setOptionLabel = useCallback((index: number) => {
		return ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingQuestion((question) => {
				if (!question) { return null; }

				return {
					...question,
					options: question.options.filter(i => i).map((option, _index) => {
						if (_index === index) {
							option.label.base = value;
						}
						return option;
					}),
				};
			});
		};
	}, []);

	function setOptionCorrect(index: number) {
		return () => {
			setWorkingQuestion((question) => {
				if (!question) { return null; }

				return {
					...question,
					options: question.options.map((option, _index) => {
						option.correct = _index === index;
						return option;
					}),
				};
			});
		};
	}

	const setWorkingQuestionTitle = useCallback(
		({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
			setWorkingQuestion((question) => {
				if (!question) { return null; }

				return {
					...question,
					title: updateTranslateKey({
						translateString: question.title,
						input: value,
						baseLanguage: workingSession!.default_language,
						language
					}),
				};
			});
		},
		[language, workingSession]
	);

	const ModalTitle = useMemo(() => {
		switch (activeStep) {
			case QuizSelectionSteps.select: {
				return "Add Quiz";
			}
			case QuizSelectionSteps.create: {
				return "Create Quiz";
			}
			case QuizSelectionSteps.questionsList: {
				return "Question List";
			}
			case QuizSelectionSteps.question: {
				return "Edit Question";
			}
			default: return "Add Quiz";
		}
	}, [activeStep]);

	const ModalSubtitle = useMemo(() => {
		switch (activeStep) {
			case QuizSelectionSteps.select: {
				return "Select a quiz to add to the page, or create a new one";
			}
			case QuizSelectionSteps.create: {
				return "Quizzes are used to test viewers of their knowledge on a given subject.";
			}
			case QuizSelectionSteps.questionsList: {
				return "Quizzes are used to test viewers of their knowledge on a given subject.";
			}
			default: return "";
		}
	}, [activeStep]);

	const ModalBody = useMemo(() => {
		switch (activeStep) {
			case QuizSelectionSteps.select: {
				return (
					<>
						<div className="modal-search">
							<TextInput
								onChange={handleSearch}
								placeholder={'Search Quizzes...'}
								icon={ICONS.SEARCH}
								size={'small'}
								id="search-quizzes"
							/>
							<div className="quiz-search-separator"> or </div>
							<button className="small" onClick={createNewQuiz}>
								{'Create Quiz'}
							</button>
						</div>
						<List
							width={560}
							height={560 - 112}
							rowCount={visibleQuizzes.length}
							rowHeight={96 + 16}
							rowRenderer={RenderQuiz}
							overscanRowCount={12}
						/>
					</>
				);
			}
			case QuizSelectionSteps.edit:
			case QuizSelectionSteps.create: {
				return (
					<>
						<TextInput
							label={'Quiz Title'}
							onChange={setWorkingQuizTitle}
							defaultValue={workingQuiz?.title.base ?? ''}
							placeholder={'Enter a title for the quiz'}
							id="quiz-title"
						/>
						<TextInput
							label={'Success Message'}
							onChange={setWorkingQuizSuccess}
							defaultValue={
								workingQuiz?.success_message.base ?? ''
							}
							placeholder={
								'Example: "Congratulations, you passed!"'
							}
							id="success-message"
						/>
						<TextInput
							label={'Failure Message'}
							onChange={setWorkingQuizFailure}
							defaultValue={
								workingQuiz?.failure_message.base ?? ''
							}
							placeholder={'Example: "Sorry, you did not pass."'}
							id="failure-message"
						/>
						<TextInput
							label={'Success Threshold'}
							onChange={setSuccessThreshold}
							defaultValue={workingQuiz?.success_threshold}
							placeholder={
								'Enter number of required correct answers'
							}
							numeric
							id="success-threshold"
						/>

						<div className="allow-retake">
							<div className="allow-retake-context">
								<label>Allow viewers to retake a quiz</label>
								<span>
									Allow all attendees to retake a quiz at any
									time during the broadcast
								</span>
							</div>
							<Switch
								on={!!workingQuiz?.allow_retake}
								onClick={toggleAllowRetake}
								value={'allow-retake'}
							/>
						</div>
					</>
				);
			}
			case QuizSelectionSteps.questionsList: {
				return (
					<div className="questions-list">
						{!!workingQuiz && workingQuiz.questions.length > 0 ? (
							<DndContext
								sensors={sensors}
								collisionDetection={closestCenter}
								onDragEnd={handleDragEnd}
								modifiers={[restrictToParentElement]}
								autoScroll={false}
							>
								<SortableContext
									items={(workingQuiz!
										.questions as any[]).map(
											(question: QuizQuestion) =>
												String(question.quiz_question)
										)}
									strategy={rectSortingStrategy}
								>
									<div className="questions-list">
										{(workingQuiz!.questions as any[]).map(
											(
												question: QuizQuestion,
												index: number
											) => (
												<QuestionComponent
													key={index}
													id={String(
														question.quiz_question
													)}
													question={question}
													editQuestion={editQuestion}
													checkDeleteQuestion={removeQuestion}
												/>
											)
										)}
									</div>
								</SortableContext>
							</DndContext>
						) : (
							<p className="quiz-questions-empty">
								{t(
									'session:quizzes.No questions have been added'
								)}
							</p>
						)}

						<button className="small" onClick={addQuestion}>
							<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Add Question
						</button>
					</div>
				);
			}
			case QuizSelectionSteps.question: {
				// there are some old multi choice options that are null,
				// they are breaking the app and we need to filter them out
				const multiChoiceOptions = workingQuestion?.options?.filter(i => i) || [];
				return (
					<div className="edit-question">
						<div className="question-row">
							<TextInput
								defaultValue={workingQuestion?.title.base ?? ''}
								onChange={setWorkingQuestionTitle}
								placeholder={'Enter question'}
								label={'Your Question'}
								id="enter-question"
							/>
							<ReactSelect
								styles={reactSelectStyles({
									menu: {
										right: 0,
										top: 0,
									},
									menuList: {
										fontSize: '14px'
									},
									container: {
										display: 'flex',
										alignItems: 'center',
									},
									control: {
										borderRadius: 100,
										border: '1px solid #908CA0',
										padding: '4px',
										fontSize: '14px'
									}
								})}
								options={[
									{
										label: 'Multiple Choice',
										value: 'multiple-choice'
									},
									{
										label: 'Text Question',
										value: 'text'
									},
								]}
								defaultValue={
									workingQuestion?.multiple_choice
										? {
											label: 'Multiple Choice',
											value: 'multiple-choice'
										} : {
											label: 'Text Question',
											value: 'text'
										}
								}
								onChange={(value) => setQuestionType(value?.value)}
								tabSelectsValue={false}
								menuPortalTarget={document.body}
								closeMenuOnSelect
							/>
						</div>

						{workingQuestion?.multiple_choice && (
							<>
								{multiChoiceOptions.length < 1 ? (
									<p className="quiz-questions-empty">
										Please add at least one option.
									</p>
								) : (
									<>
										{multiChoiceOptions.map(
											(option, index) => (
												<div
													className="quiz-question-option"
													key={index}
												>
													<TextInput
														placeholder="Enter option text"
														defaultValue={
															option.label.base
														}
														onChange={setOptionLabel(
															index
														)}
														label={`Option ${index + 1}`}
														id={`enter-option-text-${option.quiz_question_option}`}
													/>
													<div className="quiz-question-correct">
														<label>Correct</label>
														<Checkbox
															checked={
																option.correct
															}
															onChange={setOptionCorrect(
																index
															)}
															value={index}
															size={16}
															activeColor={'cyan'}
														/>
													</div>
												</div>
											)
										)}
									</>
								)}
								<button className="small" onClick={addOption}>
									<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Add Option
								</button>
							</>
						)}
					</div>
				);
			}
			default: {
				return <div></div>;
			}
		}
	}, [
		activeStep,
		createNewQuiz,
		visibleQuizzes.length,
		RenderQuiz,
		setWorkingQuizTitle,
		workingQuiz,
		setWorkingQuizSuccess,
		setWorkingQuizFailure,
		sensors,
		handleDragEnd,
		t,
		addQuestion,
		editQuestion,
		workingQuestion,
		setWorkingQuestionTitle,
		setQuestionType,
		addOption,
		setOptionLabel,
		removeQuestion,
	]);

	const finishedEditingQuestion = useCallback(() => {
		setWorkingQuiz((quiz) => {
			if (!quiz) { return null; }

			return {
				...quiz,
				questions: quiz.questions.map((question) => {
					if (
						workingQuestion &&
						question.quiz_question === workingQuestion.quiz_question
					) {
						return workingQuestion;
					}

					return question;
				}),
			};
		});

		goToQuestions();
	}, [workingQuestion]);

	const saveQuiz = useCallback(async () => {
		try {
			if (!workingQuiz) { return; }

			setSaving(true);
			if ('quiz' in workingQuiz) {
				const updatedQuiz = await UpdateQuiz(token!, workingQuiz);
				dispatch(updateQuiz(updatedQuiz));
			} else {
				const newQuiz = await InsertQuiz(
					token!,
					workingQuiz as CreateQuiz
				);
				dispatch(dispatchAddQuiz(newQuiz));
			}
			setSaving(false);
			setActiveStep(QuizSelectionSteps.select);
		} catch (e) {
			console.error(e);
		}
	}, [dispatch, token, workingQuiz]);

	const ModalFooter = useMemo(() => {
		function createQuizDisabled() {
			if (workingQuiz) {
				//is disabled if the working quiz has no questions
				if (workingQuiz.questions.length < 1) {
					return true;
				}

				//is disabled if all questions are valid
				return !workingQuiz.questions.every((question) => {
					//if the question is multiple choice, there must be at least one correct answer
					if (question.multiple_choice) {
						return question.options.some(
							(option) => option?.correct
						);
					} else {
						return true;
					}
				});
			} else {
				return true;
			}
		}

		function updateQuestionDisabled() {
			//if this is a multiple choice question, there must be at least one correct answer to be considered valid
			if (workingQuestion?.multiple_choice) {
				return !workingQuestion.options.some(
					(option) => option?.correct
				);
			}

			return false;
		}

		function goToQuestionsDisabled() {
			return !(
				workingQuiz?.title.base &&
				workingQuiz?.success_message.base &&
				workingQuiz?.failure_message.base
			);
		}

		switch (activeStep) {
			case QuizSelectionSteps.select: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={doneSelectingQuizzes}
							className="primary"
						>
							Done
						</button>
					</>
				);
			}

			case QuizSelectionSteps.edit:
			case QuizSelectionSteps.create: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={goToQuestions}
							disabled={goToQuestionsDisabled()}
							className="primary"
						>
							Go to Questions
						</button>
					</>
				);
			}

			case QuizSelectionSteps.questionsList: {
				return workingQuiz && !('quiz' in workingQuiz) ? (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={saveQuiz}
							className="primary"
							disabled={createQuizDisabled()}
						>
							Create Quiz
						</button>
					</>
				) : (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={saveQuiz}
							className="primary"
							disabled={createQuizDisabled()}
						>
							{saving ? <WaitingIndicator /> : 'Save Quiz'}
						</button>
					</>
				);
			}

			case QuizSelectionSteps.question: {
				return workingQuestion &&
					!('quiz_question' in workingQuestion) ? (
					<>
						<button
							onClick={goToQuestions}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={finishedEditingQuestion}
							className="primary"
							disabled={updateQuestionDisabled()}
						>
							<Icon name={ICONS.ADD} size={12} color={COLORS.WHITE} /> Add Question
						</button>
					</>
				) : (
					<>
						<button
							onClick={goToQuestions}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={finishedEditingQuestion}
							className="primary"
							disabled={updateQuestionDisabled()}
						>
							Edit Question
						</button>
					</>
				);
			}

			default: {
				return (
					<>
						<button
							onClick={closeModal}
							style={{ marginRight: 16 }}
						>
							Cancel
						</button>
						<button
							onClick={doneSelectingQuizzes}
							className="primary"
						>
							Done
						</button>
					</>
				);
			}
		}
	}, [
		activeStep,
		workingQuiz,
		workingQuestion,
		doneSelectingQuizzes,
		saveQuiz,
		saving,
		finishedEditingQuestion,
	]);

	return page_module.is_on && (!workingSession?.layout?.withMenu || page_module) ? (
		<div className={classNames(
			"editor-wrapper quizzes",
			backgroundColor,
			template
		)}
			style={backgroundImg}
		>
			<Controls
				page_module={page_module}
				openEditModal={() => setModalOpen(true)}
			/>
			<div className="container">
				<EditableCustomTitle
					moduleTitle="Quizzes"
					element="h5"
					content={page_module?.content || null}
					pageModule={page_module}
				/>
				<div className="quizzes-content">
					{page_module.modules?.map((quiz: Quiz) => (
						<QuizItem key={quiz.quiz} quiz={quiz} language={language} />
					))}

					<LargeButton onClick={addQuiz} title={'Add Quiz'} />
				</div>
			</div>
			<ModalComponent
				cancellable={true}
				closeable={false}
				open={modalOpen}
				onRequestClose={closeModal}
				title={ModalTitle}
				subtitle={ModalSubtitle}
				footer={ModalFooter}
			>
				{ModalBody}
			</ModalComponent>
		</div>
	) : null;
}

interface SortableQuestion {
	id: string;
	question: QuizQuestion;
	editQuestion: (question: QuizQuestion) => () => void;
	checkDeleteQuestion: (question: QuizQuestion) => void;
}

function QuestionComponent(props: SortableQuestion) {
	const { attributes, listeners, setNodeRef, transform } = useSortable({
		id: props.id,
	});

	const { question, editQuestion, checkDeleteQuestion } = props;

	const style = {
		transform: CSS.Transform.toString(transform),
		transition: 'none',
	};

	const validMultiple = useMemo(() => {
		if (question.multiple_choice) {
			return question.options.some((option) => option?.correct);
		}

		return true;
	}, [question]);

	return (
		<div
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
			className="quiz-question"
		>
			<div className="drag-handle">
				<Icon
					name={ICONS.DRAG_HANDLE}
					size={18}
					color={COLORS.DEFAULT_GRAY}
				/>
			</div>
			<div className="quiz-question-info">
				{question.title.base}
				<span className="subtitle">
					{question.multiple_choice ? 'Multiple choice' : 'Text area'}{' '}
					{!validMultiple && (
						<span className="invalid-multiple">
							(correct option required)
						</span>
					)}
				</span>
			</div>
			<div className="quiz-question-actions">
				<button className="no-style" onClick={editQuestion(question)}>
					<Icon name={ICONS.EDIT} size={16} color={COLORS.WHITE} />
				</button>
				<button className="no-style" onClick={() => checkDeleteQuestion(question)}>
					<Icon name={ICONS.TRASH} size={16} color={COLORS.WHITE} />
				</button>
			</div>
		</div>
	);
}
